import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getReport } from '../api/userApi'
import { FiSearch, FiLoader } from 'react-icons/fi'
import Pagination from './Pagination'
import { handleDate } from '../handleDate'
import { handleDate as handleDateWithTime } from '../handleDateAndPdf'

export default function PaymentStatus() {
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)

  // 🔹 Fetch data from API
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['paymentStatus', page],
    queryFn: () => getReport(page),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const reports = data?.payments || []
  const totalPages = Math.ceil((data?.totalCount || 0) / 9)

  // 🔹 Search Filter
  const filteredData = useMemo(() => {
    return reports.filter(
      (item) =>
        item.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.status?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [searchTerm, reports])

  // 🔹 Loading State
  if (isPending) {
    return (
      <div className="flex justify-center items-center h-80">
        <FiLoader className="w-8 h-8 animate-spin text-orange-500" />
        <span className="ml-3 text-gray-600 text-lg font-medium">
          Loading invoices...
        </span>
      </div>
    )
  }

  // 🔹 Error State
  if (isError) {
    return (
      <div className="text-center text-red-600 mt-20">
        <p className="text-lg font-semibold">Failed to load data 😞</p>
        <p className="text-gray-500">{error?.message}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <h2 className="text-2xl font-semibold text-gray-800 text-center mb-6">
        🧾 Payment & Order Status
      </h2>

      {/* Search Bar */}
      <div className="max-w-md mx-auto mb-6 flex items-center bg-white border rounded-full shadow-sm px-4 py-2">
        <FiSearch className="text-gray-400 w-5 h-5 mr-2" />
        <input
          type="text"
          placeholder="Search by username, order ID, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border-none focus:ring-0 focus:outline-none w-full text-gray-700"
        />
      </div>

      {/* Data Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredData.length > 0 ? (
          filteredData.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-gray-200 shadow-md hover:shadow-lg transition rounded-2xl p-5"
            >
              <div className="flex justify-between items-center mb-3">
                <span className="font-semibold text-gray-700">
                  {item.userName}
                </span>
                <span
                  className={`px-2 py-1 text-sm rounded-full ${
                    item.status === 'PENDING'
                      ? 'bg-yellow-100 text-yellow-700'
                      : item.status === 'SUCCESS'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {item.status}
                </span>
              </div>

              <p className="text-sm text-gray-500 mb-2">
                <strong>Code:</strong> {item.responseCode}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Txn Id:</strong> {item.txnId}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Message:</strong> {item.responseMessage}
              </p>
              <div className="text-sm text-gray-500 mb-2">
                <strong>Response:</strong>
                <pre className="mt-1 bg-slate-50 text-gray-700 p-2 rounded-md overflow-x-auto whitespace-pre-wrap break-all text-xs">
                  {item.rawResponse}
                </pre>
              </div>

              <p className="text-sm text-gray-500 mb-2">
                <strong>Order ID:</strong> {item.orderId}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Country:</strong> {item.country}, {item.state}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>Amount:</strong> ₹{item.amount}
              </p>
              <p className="text-sm text-gray-500 mb-2">
                <strong>User Email:</strong> {item.userEmail}
              </p>

              <p className="text-sm text-gray-500 mb-2">
                <strong>User Phone:</strong> {item.userPhone}
              </p>

              {/* Products */}
              <div className="mt-4 bg-gray-50 p-3 rounded-lg hover:bg-orange-50">
                <h4 className="font-medium text-gray-700 mb-2">🛍 Products:</h4>
                {item.products.map((prod, idx) => (
                  <div key={idx} className="text-sm text-gray-600 mb-1">
                    <p>
                      <strong>{prod.productName}</strong> — {prod.insight}
                    </p>
                    <p className="text-gray-500">
                      ₹{prod.price} |{' '}
                      {handleDate(prod.date) === 'Invalid Date'
                        ? prod.date
                        : handleDate(prod.date)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-sm text-gray-400">
                Txn Start: {handleDateWithTime(item.createdAt)}
              </div>

              <div className="mt-1 text-sm text-gray-400">
                Txn Completed : {handleDateWithTime(item.updatedAt)}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-500 mt-10">
            No records found 😶
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-8 flex justify-center">
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </div>
    </div>
  )
}
